import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AIRequestContext {
  [key: string]: any;
}

interface AIMessage {
  role: 'user' | 'assistant';
  content: string;
}

export function useAIAssistant() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateResponse = useCallback(async (
    type: 'lead_score' | 'email_generation' | 'call_analysis' | 'deal_insights' | 'chat',
    context: AIRequestContext,
    messages?: AIMessage[]
  ): Promise<string | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('ai-assistant', {
        body: { type, context, messages },
      });

      if (fnError) {
        throw new Error(fnError.message);
      }

      if (data?.error) {
        if (data.error.includes('Rate limit')) {
          toast.error('AI rate limit reached. Please try again in a moment.');
        } else if (data.error.includes('credits')) {
          toast.error('AI credits exhausted. Please add credits to continue.');
        } else {
          toast.error(data.error);
        }
        throw new Error(data.error);
      }

      return data?.content || null;
    } catch (err: any) {
      console.error('AI Assistant error:', err);
      setError(err.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const scoreLead = useCallback(async (lead: any) => {
    return generateResponse('lead_score', {
      firstName: lead.first_name,
      lastName: lead.last_name,
      companyName: lead.company_name,
      email: lead.email,
      source: lead.source,
      status: lead.status,
      tags: lead.tags,
    });
  }, [generateResponse]);

  const generateEmail = useCallback(async (context: {
    recipientName: string;
    companyName: string;
    emailType?: string;
    context?: string;
    previousInteraction?: string;
  }) => {
    return generateResponse('email_generation', context);
  }, [generateResponse]);

  const analyzeCall = useCallback(async (call: any) => {
    return generateResponse('call_analysis', {
      duration: call.duration_seconds,
      direction: call.direction,
      notes: call.notes,
      transcript: call.transcript,
    });
  }, [generateResponse]);

  const getDealInsights = useCallback(async (deal: any, stageName: string, accountName: string) => {
    return generateResponse('deal_insights', {
      title: deal.title,
      amount: deal.amount,
      stage: stageName,
      expectedCloseDate: deal.expected_close_date,
      accountName,
    });
  }, [generateResponse]);

  const chat = useCallback(async (query: string, messages?: AIMessage[]) => {
    return generateResponse('chat', { query }, messages);
  }, [generateResponse]);

  return {
    isLoading,
    error,
    scoreLead,
    generateEmail,
    analyzeCall,
    getDealInsights,
    chat,
  };
}
